import React, { useState, useEffect } from 'react';
import { callApi, callPublicApi } from '../api';
import './YonetimSayfasi.css';
import { Search, ChevronDown, ChevronUp, UserX, MessageCircle, Phone, Fingerprint, CalendarDays, KeyRound, Building2, Shield, Sliders, X, CheckCircle } from 'lucide-react';

import UyeDogrulama from '../components/UyeDogrulama';

const ITEMS_PER_PAGE = 40;

const UyeYonetim = () => {
    const [tumUyeler, setTumUyeler] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('kayitTarihi');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Güven Skoru Modalları
    const [selectedUyeForScore, setSelectedUyeForScore] = useState(null);
    const [scoreHistory, setScoreHistory] = useState([]);
    const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [trustSettings, setTrustSettings] = useState({});
    const [adjustmentForm, setAdjustmentForm] = useState({ puanDegisimi: 10, aciklama: '' });
    const [modalLoading, setModalLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchUyeler();
    }, []);

    const fetchUyeler = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await callApi('/uyeler', 'GET');
            const uyeItems = Array.isArray(data) ? data : data.items || [];
            setTumUyeler(uyeItems);
            setCurrentPage(1);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (uye) => {
        const { uyeID, ad, soyad } = uye;
        if (!uyeID) {
            alert("HATA: Bu üyenin ID bilgisi (uyeID) bulunamadı. Silinemez.");
            return;
        }

        if (!window.confirm(`Ad: ${ad} ${soyad}\nBu üyeyi silmek istediğinizden emin misiniz?`)) {
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            await callApi(`/uyeler?uyeID=${uyeID}`, 'DELETE');
            alert("Üye başarıyla silindi!");
            fetchUyeler();
        } catch (err) {
            console.error("Silme hatası:", err);
            setError(err.message || "Üye silme işlemi başarısız oldu.");
            setIsLoading(false);
        }
    };

    const handleSendWhatsapp = async (uye) => {
        if (!uye.telefon) {
            alert("Bu üyenin telefon numarası yok.");
            return;
        }

        if (!window.confirm(`${uye.ad} kişisine dijital kart linki WhatsApp üzerinden gönderilsin mi?`)) return;

        let phone = uye.telefon.replace(/\D/g, '');
        if (phone.startsWith('0')) phone = phone.substring(1);
        if (phone.length === 10) phone = '90' + phone;

        const message = `Merhaba ${uye.ad}, Klügastronomi Dijital Üye Kartın burada: https://klugastronomi.com.tr/kimlik?code=${uye.uyeKodu || 'YOK'}`;
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    // --- GÜVEN SKORU DÖKÜMÜ & ADMIN DÜZELTME ---
    const handleOpenScoreModal = async (uye) => {
        setSelectedUyeForScore(uye);
        setIsScoreModalOpen(true);
        setModalLoading(true);
        setScoreHistory([]);
        setAdjustmentForm({ puanDegisimi: 10, aciklama: '' });
        setSuccessMessage('');

        try {
            const res = await callPublicApi(`/guven-skoru/gecmis?uyeID=${uye.uyeID || uye.uyeKodu}`);
            if (res && res.gecmis) {
                setScoreHistory(res.gecmis);
                if (res.uye && res.uye.guvenSkoru !== undefined) {
                    setSelectedUyeForScore(prev => ({ ...prev, guvenSkoru: res.uye.guvenSkoru }));
                }
            }
        } catch (e) {
            console.error("Skor geçmişi alınamadı:", e);
        } finally {
            setModalLoading(false);
        }
    };

    const handleApplyScoreAdjustment = async (e) => {
        e.preventDefault();
        if (!selectedUyeForScore) return;

        try {
            const res = await callApi('/guven-skoru/admin-duzelt', 'POST', {
                uyeID: selectedUyeForScore.uyeID,
                puanDegisimi: Number(adjustmentForm.puanDegisimi),
                aciklama: adjustmentForm.aciklama || "Yönetici manuel puan düzeltmesi"
            });

            if (res && res.success) {
                setSuccessMessage(`Skor güncellendi: ${res.oncekiSkor} ➔ ${res.yeniSkor}`);
                setSelectedUyeForScore(prev => ({ ...prev, guvenSkoru: res.yeniSkor }));
                // Listeyi de güncelle
                setTumUyeler(prev => prev.map(u => u.uyeID === selectedUyeForScore.uyeID ? { ...u, guvenSkoru: res.yeniSkor } : u));
                // Geçmişi yeniden çek
                const updatedHistory = await callPublicApi(`/guven-skoru/gecmis?uyeID=${selectedUyeForScore.uyeID}`);
                if (updatedHistory && updatedHistory.gecmis) setScoreHistory(updatedHistory.gecmis);
            }
        } catch (err) {
            alert("Puan düzeltme hatası: " + err.message);
        }
    };

    // --- PUANLAMA KATSAYILARI AYARLARI ---
    const handleOpenSettingsModal = async () => {
        setIsSettingsModalOpen(true);
        setModalLoading(true);
        try {
            const res = await callPublicApi('/guven-skoru/ayarlar');
            if (res && res.settings) {
                const s = {};
                Object.entries(res.settings).forEach(([k, v]) => {
                    s[k] = v.deger;
                });
                setTrustSettings(s);
            }
        } catch (e) {
            console.error("Ayarlar yüklenemedi:", e);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        try {
            await callApi('/guven-skoru/ayarlar', 'PUT', trustSettings);
            alert("Güven skoru katsayıları başarıyla güncellendi!");
            setIsSettingsModalOpen(false);
        } catch (err) {
            alert("Katsayılar kaydedilemedi: " + err.message);
        }
    };

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder(key === 'kayitTarihi' ? 'desc' : 'asc');
        }
    };

    // FİLTRELEME & ARAMA
    const filteredUyeler = tumUyeler.filter(uye => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;
        return (
            (uye.ad && uye.ad.toLowerCase().includes(term)) ||
            (uye.soyad && uye.soyad.toLowerCase().includes(term)) ||
            (uye.ogrenciNo && uye.ogrenciNo.toLowerCase().includes(term)) ||
            (uye.uyeKodu && uye.uyeKodu.toLowerCase().includes(term)) ||
            (uye.bolum && uye.bolum.toLowerCase().includes(term)) ||
            (uye.telefon && uye.telefon.includes(term))
        );
    });

    // SIRALAMA
    const sortedUyeler = [...filteredUyeler].sort((a, b) => {
        let valA = a[sortBy] || '';
        let valB = b[sortBy] || '';

        if (sortBy === 'kayitTarihi') {
            valA = new Date(valA).getTime() || 0;
            valB = new Date(valB).getTime() || 0;
        } else if (sortBy === 'guvenSkoru') {
            valA = a.guvenSkoru !== undefined ? Number(a.guvenSkoru) : 100;
            valB = b.guvenSkoru !== undefined ? Number(b.guvenSkoru) : 100;
        } else {
            valA = valA.toString().toLowerCase();
            valB = valB.toString().toLowerCase();
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
    });

    // SAYFALAMA
    const totalPages = Math.ceil(sortedUyeler.length / ITEMS_PER_PAGE) || 1;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentItems = sortedUyeler.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    if (isLoading && tumUyeler.length === 0) {
        return (
            <div className="loading-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <div className="spinner"></div>
                <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Üyeler yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className="yonetim-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* ÜST BAŞLIK & İSTATİSTİK ALANI */}
            <div className="yonetim-header" style={{ marginBottom: '24px' }}>
                <div>
                    <h2 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                        Üye Yönetimi
                    </h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '0.95rem' }}>
                        Toplam {tumUyeler.length} kayıtlı üye | Sayfa {currentPage} / {totalPages}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleOpenSettingsModal}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '12px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                        <Sliders size={16} /> Skor Katsayıları
                    </button>
                    <UyeDogrulama />
                </div>
            </div>

            {error && (
                <div className="error-message" style={{ padding: '12px 16px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: '12px', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {/* ARAMA ÇUBUĞU */}
            <div style={{ position: 'relative', marginBottom: '16px' }}>
                <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                    type="text"
                    placeholder="İsim, numara, telefon, bölüm veya üye koduna göre ara..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    style={{ width: '100%', padding: '12px 16px 12px 46px', borderRadius: '14px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.95rem' }}
                />
            </div>

            {/* SIRALAMA SEÇENEKLERİ */}
            <div className="sort-controls" style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '12px' }}>
                {[
                    { k: 'kayitTarihi', label: 'Kayıt Tarihi' },
                    { k: 'guvenSkoru', label: 'Güven Skoru' },
                    { k: 'ad', label: 'İsim' },
                    { k: 'ogrenciNo', label: 'Öğrenci No' },
                    { k: 'bolum', label: 'Bölüm' }
                ].map(item => (
                    <button
                        key={item.k}
                        onClick={() => handleSort(item.k)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: '1px solid var(--border-color)',
                            background: sortBy === item.k ? 'var(--primary, #ea580c)' : 'var(--bg-card)',
                            color: sortBy === item.k ? '#fff' : 'var(--text-secondary)',
                            fontWeight: sortBy === item.k ? 700 : 500,
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {item.label}
                        {sortBy === item.k && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </button>
                ))}
            </div>

            {/* ÜYE KARTLARI IZGARASI */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                {currentItems.length > 0 ? (
                    currentItems.map((uye) => {
                        const score = uye.guvenSkoru !== undefined ? Number(uye.guvenSkoru) : 100;
                        const isScoreGood = score >= 80;
                        const isScoreMed = score >= 50 && score < 80;
                        const scoreColor = isScoreGood ? '#10b981' : isScoreMed ? '#f59e0b' : '#ef4444';
                        const scoreBg = isScoreGood ? 'rgba(16, 185, 129, 0.1)' : isScoreMed ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)';

                        return (
                            <div key={uye.uyeID || uye.ogrenciNo} style={{
                                background: 'var(--bg-card)',
                                borderRadius: '20px',
                                padding: '20px',
                                border: '1px solid var(--border-color)',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    {/* Kart Başlık */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: 'var(--primary-bg, rgba(234, 88, 12, 0.1))', color: 'var(--primary, #ea580c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 800 }}>
                                                {uye.ad ? uye.ad.charAt(0).toUpperCase() : '?'}
                                            </div>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                                                    {uye.ad} {uye.soyad}
                                                </h3>
                                                <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                                    <CalendarDays size={12} />
                                                    {uye.kayitTarihi ? new Date(uye.kayitTarihi).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* GÜVEN SKORU ROZETİ */}
                                        <div
                                            onClick={() => handleOpenScoreModal(uye)}
                                            style={{
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                background: scoreBg,
                                                color: scoreColor,
                                                border: `1px solid ${scoreColor}40`,
                                                fontSize: '0.75rem',
                                                fontWeight: 800,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                cursor: 'pointer'
                                            }}
                                            title="Güven Skorunu İncele ve Düzenle"
                                        >
                                            <Shield size={13} />
                                            {score} Puan
                                        </div>
                                    </div>

                                    {/* Bilgi Kutusu */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '14px' }}>
                                        <div>
                                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Öğrenci No</span>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Fingerprint size={13} /> {uye.ogrenciNo}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Telefon</span>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Phone size={13} /> {uye.telefon || '-'}
                                            </div>
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Bölüm</span>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Building2 size={13} /> {uye.bolum || 'Belirtilmemiş'}
                                            </div>
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Üye Kodu</span>
                                            <div style={{ fontSize: '0.85rem', color: 'var(--primary, #ea580c)', fontFamily: 'monospace', fontWeight: 800 }}>
                                                <KeyRound size={13} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                                                {uye.uyeKodu || 'YOK'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Aksiyonlar */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                                    <button
                                        onClick={() => handleOpenScoreModal(uye)}
                                        style={{ padding: '8px', borderRadius: '10px', background: scoreBg, color: scoreColor, border: `1px solid ${scoreColor}40`, fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer' }}
                                        title="Güven Skoru Geçmişi & Mazeret Düzeltmesi"
                                    >
                                        <Shield size={14} /> Skor & Mazeret
                                    </button>
                                    <button
                                        onClick={() => handleSendWhatsapp(uye)}
                                        style={{ padding: '8px', borderRadius: '10px', background: '#25D366', color: 'white', border: 'none', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer' }}
                                    >
                                        <MessageCircle size={14} /> WhatsApp
                                    </button>
                                    <button
                                        onClick={() => handleDelete(uye)}
                                        style={{ padding: '8px', borderRadius: '10px', background: '#fee2e2', color: '#ef4444', border: 'none', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer' }}
                                    >
                                        <UserX size={14} /> Sil
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', background: 'var(--bg-card)', borderRadius: '20px', color: 'var(--text-muted)' }}>
                        Kayıtlı üye bulunamadı.
                    </div>
                )}
            </div>

            {/* SAYFALAMA */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '40px' }}>
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'inherit', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                    >
                        Önceki
                    </button>
                    <span style={{ padding: '8px 16px', fontWeight: 700 }}>{currentPage} / {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'inherit', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                    >
                        Sonraki
                    </button>
                </div>
            )}

            {/* MODAL: ÜYE GÜVEN SKORU DÖKÜMÜ & ADMIN MAZERET DÜZELTMESİ */}
            {isScoreModalOpen && selectedUyeForScore && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '650px' }}>
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Shield color="#ea580c" size={20} />
                                    {selectedUyeForScore.ad} {selectedUyeForScore.soyad} — Güven Skoru
                                </h3>
                                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                    Öğrenci No: {selectedUyeForScore.ogrenciNo} | Üye Kodu: {selectedUyeForScore.uyeKodu || 'YOK'}
                                </p>
                            </div>
                            <button onClick={() => setIsScoreModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            {/* Anlık Skor Göstergesi */}
                            <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700 }}>Mevcut Güven Skoru</span>
                                    <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--primary, #ea580c)' }}>
                                        {selectedUyeForScore.guvenSkoru !== undefined ? selectedUyeForScore.guvenSkoru : 100} <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 400 }}>/ 100</span>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    {Number(selectedUyeForScore.guvenSkoru || 100) < 20 ? (
                                        <span style={{ color: '#ef4444', fontWeight: 700 }}>🚫 Kayıt Askıda (Kritik)</span>
                                    ) : Number(selectedUyeForScore.guvenSkoru || 100) < 50 ? (
                                        <span style={{ color: '#f59e0b', fontWeight: 700 }}>⚠️ Öncelik Kaybı (Sadece Yedek)</span>
                                    ) : (
                                        <span style={{ color: '#10b981', fontWeight: 700 }}>✅ Normal Katılımcı (Öncelikli)</span>
                                    )}
                                </div>
                            </div>

                            {successMessage && (
                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#dcfce7', border: '1px solid #86efac', color: '#166534', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                                    <CheckCircle size={16} /> {successMessage}
                                </div>
                            )}

                            {/* Puan Düzeltme Formu (Mazeret İtirazı) */}
                            <form onSubmit={handleApplyScoreAdjustment} style={{ padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                                    🛡️ Mazeret Onayı / Manuel Puan Düzeltmesi
                                </h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Puan Değişimi (+ veya -)</label>
                                        <input
                                            type="number"
                                            required
                                            value={adjustmentForm.puanDegisimi}
                                            onChange={e => setAdjustmentForm({ ...adjustmentForm, puanDegisimi: e.target.value })}
                                            placeholder="+15 veya -10"
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'inherit' }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Düzeltme Gerekçesi / Mazeret Notu *</label>
                                        <input
                                            type="text"
                                            required
                                            value={adjustmentForm.aciklama}
                                            onChange={e => setAdjustmentForm({ ...adjustmentForm, aciklama: e.target.value })}
                                            placeholder="Örn: Sınav çakışması mazereti kabul edildi +25 puan"
                                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'inherit' }}
                                        />
                                    </div>
                                </div>
                                <button type="submit" className="primary-btn" style={{ width: '100%', padding: '10px', borderRadius: '8px', fontSize: '0.85rem' }}>
                                    Puan Düzeltmesini Kaydet ve Deftere İşle
                                </button>
                            </form>

                            {/* Değiştirilemez Hareket Geçmişi */}
                            <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                                Denetim Günlüğü ({scoreHistory.length} İşlem)
                            </h4>

                            {modalLoading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>Yükleniyor...</div>
                            ) : scoreHistory.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                    Bu üye için henüz kayıtlı bir skor hareketi bulunmuyor.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {scoreHistory.map(log => (
                                        <div key={log.id} style={{ padding: '10px 14px', borderRadius: '10px', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
                                            <div>
                                                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                                                    {log.islemTipi === 'KATILIM_BASARILI' && '✅ Başarılı Katılım'}
                                                    {log.islemTipi === 'ZAMANINDA_IPTAL' && '⌛ Zamanında İptal'}
                                                    {log.islemTipi === 'GEC_IPTAL' && '⚠️ Geç İptal'}
                                                    {log.islemTipi === 'NO_SHOW' && '❌ Etkinliğe Katılmama (No-Show)'}
                                                    {log.islemTipi === 'ADMIN_DUZELTME' && '🛡️ Yönetici Düzeltmesi'}
                                                    {log.islemTipi === 'BASLANGIC' && '🌟 Başlangıç'}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                    {log.aciklama} {log.etkinlikBaslik ? `(${log.etkinlikBaslik})` : ''}
                                                </div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                                    İşleyen: {log.olusturan || 'SİSTEM'} | {new Date(log.tarih).toLocaleString('tr-TR')}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span style={{ fontWeight: 900, fontSize: '0.95rem', color: log.puanDegisimi > 0 ? '#10b981' : log.puanDegisimi === 0 ? 'var(--text-muted)' : '#ef4444' }}>
                                                    {log.puanDegisimi > 0 ? `+${log.puanDegisimi}` : log.puanDegisimi}
                                                </span>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                                    {log.oncekiSkor} ➔ {log.sonrakiSkor}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="secondary-btn" onClick={() => setIsScoreModalOpen(false)}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: PUANLAMA KATSAYILARI (PARAMETRİK AYARLAR) */}
            {isSettingsModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '550px' }}>
                        <div className="modal-header">
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Sliders size={20} color="#ea580c" />
                                Güven Skoru Katsayıları
                            </h3>
                            <button onClick={() => setIsSettingsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSaveSettings}>
                            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                                    Puanlama sisteminin kurallarını buradan dilediğiniz gibi güncelleyebilirsiniz. Tüm hesaplamalar sunucu tarafında otomatik gerçekleşir.
                                </p>

                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                                        Başarılı Katılım Ödülü (QR Okutma) (+)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={trustSettings.ATTENDANCE_REWARD || 10}
                                        onChange={e => setTrustSettings({ ...trustSettings, ATTENDANCE_REWARD: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                                        Mazeretsiz etkinliğe katılmama  (-)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={trustSettings.NO_SHOW_PENALTY || 25}
                                        onChange={e => setTrustSettings({ ...trustSettings, NO_SHOW_PENALTY: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                                        Geç / Son Dakika İptal Cezası (-)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="50"
                                        value={trustSettings.LATE_CANCEL_PENALTY || 10}
                                        onChange={e => setTrustSettings({ ...trustSettings, LATE_CANCEL_PENALTY: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                                        Öncelik Kısıtlama Eşiği (Bu puanın altı asil kontenjana giremez, sadece yedeğe yazılır)
                                    </label>
                                    <input
                                        type="number"
                                        min="10"
                                        max="90"
                                        value={trustSettings.RESTRICTION_THRESHOLD || 50}
                                        onChange={e => setTrustSettings({ ...trustSettings, RESTRICTION_THRESHOLD: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: '4px' }}>
                                        Kayıt Askıya Alma Eşiği (Bu puanın altı etkinliklere kaydolamaz)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="50"
                                        value={trustSettings.BAN_THRESHOLD || 20}
                                        onChange={e => setTrustSettings({ ...trustSettings, BAN_THRESHOLD: Number(e.target.value) })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="secondary-btn" onClick={() => setIsSettingsModalOpen(false)}>İptal</button>
                                <button type="submit" className="primary-btn">Katsayıları Güncelle</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UyeYonetim;